import React, { forwardRef, useId } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Input as DesignSystemInput,
  InputVariant,
} from '../../design-system/components/Input';
import { LangLabelSlot } from '../../design-system/patterns/FormLayout';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';
const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;
const RequiredIndicator = styled.span`
  color: ${props => props.theme.colors.error};
  margin-left: ${spacing[1]};
  font-weight: ${typography.fontWeight.bold};
`;
const TextArea = styled.textarea`
  padding: ${spacing[3]} ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  transition: ${animations.transition(
    ['border-color', 'box-shadow', 'background-color'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  resize: vertical;
  min-height: 100px;
  line-height: ${typography.lineHeight.normal};
  width: 100%;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.colored.sm(props.theme.colors.primary)};
  }
  &:disabled {
    background: ${props => props.theme.colors.surfaceHover};
    color: ${props => props.theme.colors.textSecondary};
    cursor: not-allowed;
  }
  &::placeholder {
    color: ${props => props.theme.colors.textMuted};
  }
`;
const Select = styled.select`
  padding: ${spacing[3]} ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  transition: ${animations.transition(
    ['border-color', 'box-shadow', 'background-color'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  cursor: pointer;
  width: 100%;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.colored.sm(props.theme.colors.primary)};
  }
  &:disabled {
    background: ${props => props.theme.colors.surfaceHover};
    color: ${props => props.theme.colors.textSecondary};
    cursor: not-allowed;
  }
`;
const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
`;
const SelectLabel = styled.label<{ error: boolean; required: boolean; $labelSize?: 'sm' | 'base' }>`
  font-weight: ${typography.fontWeight.semibold};
  color: ${props =>
    props.error ? props.theme.colors.error : props.theme.colors.text};
  font-size: ${props => (props.$labelSize === 'sm' ? typography.fontSize.xs : typography.fontSize.sm)};
  font-family: ${typography.fontFamily.primary};
`;
const ErrorMessage = styled.span`
  color: ${props => props.theme.colors.error};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
  margin-top: -${spacing[1]};
`;
interface FormFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  textarea?: boolean;
  select?: boolean;
  options?: { value: string; label: string }[];
  required?: boolean;
  variant?: InputVariant;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  /** When 'sm', renders the label with smaller font (e.g. for date of birth). */
  labelSize?: 'sm' | 'base';
  /**
   * Reserve an invisible language-badge slot so this field's control aligns with
   * neighboring BilingualDoubleInput fields in the same row.
   */
  alignWithBilingual?: boolean;
}
export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  (
    {
      label,
      error,
      helperText,
      textarea,
      select,
      options,
      required = false,
      variant = 'outlined',
      iconLeft,
      iconRight,
      labelSize,
      alignWithBilingual = false,
      id,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const reactId = useId();
    // Prefer explicit id, then name (for autofill), then a stable unique id — never "field".
    const fieldId = id || props.name || `field-${reactId}`;

    if (textarea) {
      const errorId = error ? `${fieldId}-error` : undefined;
      const helperId = helperText && !error ? `${fieldId}-helper` : undefined;
      return (
        <FieldContainer>
          {label && (
            <SelectLabel as="label" htmlFor={fieldId} error={!!error} required={required} $labelSize={labelSize}>
              {label}
              {required && <RequiredIndicator>*</RequiredIndicator>}
            </SelectLabel>
          )}
          {alignWithBilingual && <LangLabelSlot />}
          <TextArea
            {...(props as any)}
            id={fieldId}
            aria-required={required}
            aria-invalid={!!error}
            aria-describedby={[errorId, helperId].filter(Boolean).join(' ') || undefined}
          />
          {error && (
            <ErrorMessage id={errorId} role="alert">
              {error}
            </ErrorMessage>
          )}
          {helperText && !error && (
            <ErrorMessage as="span" id={helperId} style={{ color: theme.colors.textSecondary }}>
              {helperText}
            </ErrorMessage>
          )}
        </FieldContainer>
      );
    }

    if (select) {
      const errorId = error ? `${fieldId}-error` : undefined;
      const helperId = helperText && !error ? `${fieldId}-helper` : undefined;
      const SelectRoot = alignWithBilingual ? FieldContainer : SelectWrapper;
      return (
        <SelectRoot>
          {label && (
            <SelectLabel as="label" htmlFor={fieldId} error={!!error} required={required} $labelSize={labelSize}>
              {label}
              {required && <RequiredIndicator>*</RequiredIndicator>}
            </SelectLabel>
          )}
          {alignWithBilingual && <LangLabelSlot />}
          <Select
            {...(props as any)}
            id={fieldId}
            aria-required={required}
            aria-invalid={!!error}
            aria-describedby={[errorId, helperId].filter(Boolean).join(' ') || undefined}
          >
            {options
              ?.filter((opt, index, arr) => arr.findIndex(o => o.value === opt.value) === index)
              .map(opt => (
              <option key={opt.value === '' ? '__placeholder' : opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          {error && (
            <ErrorMessage id={errorId} role="alert">
              {error}
            </ErrorMessage>
          )}
          {helperText && !error && (
            <ErrorMessage as="span" id={helperId} style={{ color: theme.colors.textSecondary }}>
              {helperText}
            </ErrorMessage>
          )}
        </SelectRoot>
      );
    }

    // Always use an external <label htmlFor> so accessible names stay correct
    // (avoids nested/orphaned labels when DesignSystemInput also renders a label).
    const errorId = error ? `${fieldId}-error` : undefined;
    const helperId = helperText && !error ? `${fieldId}-helper` : undefined;
    const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;

    const labelText = label ? (required ? `${label} *` : label) : undefined;

    return (
      <FieldContainer>
        {labelText && (
          <SelectLabel
            as="label"
            htmlFor={fieldId}
            error={!!error}
            required={required}
            $labelSize={labelSize === 'sm' ? 'sm' : 'base'}
          >
            {labelText}
          </SelectLabel>
        )}
        {alignWithBilingual && <LangLabelSlot />}
        <DesignSystemInput
          ref={ref}
          error={!!error}
          helperText={error || helperText}
          variant={variant}
          iconLeft={iconLeft}
          iconRight={iconRight}
          required={required}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          aria-label={!label ? props['aria-label'] || props.name : undefined}
          {...props}
          id={fieldId}
          name={props.name ?? fieldId}
        />
      </FieldContainer>
    );
  }
);
FormField.displayName = 'FormField';
export default FormField;
